import ListFilters from "@/common/components/ListFilters"
import { SheetFilters } from "@/common/components/SheetFilters"
import TextField from "@/common/components/TextField"
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker"
import { internalBaseEquipments } from "@/common/constants/internalBaseEquipments"
import { useCompanyEquipments } from "@/common/hooks/queries/useCompanyEquipments"
import { useCompanyUsers } from "@/common/hooks/queries/useCompanyUsers"
import { useProducts } from "@/common/hooks/queries/useProducts"
import useDebounce from "@/common/hooks/useDebounce"
import { useFilterDate } from "@/common/hooks/useFilterDate"
import { useFilterParams } from "@/common/hooks/useFilterParams"
import { Mapper } from "@/common/services/mapper"
import { CompanyBranchData } from "@/common/types/companyBranches.type"
import { EquipmentData } from "@/features/cards/types/CardDetails.type"
import { useEstablishmentCompanies } from "@/features/establishments/hooks/useEstablishmentCompanies"
import { EstablishmentCompanyTransformer } from "@/features/establishments/services/establishmentCompanyTransformer"
import { useAvailableCompanyBranches } from "@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches"
import { CompanyUserData } from "@/features/privateCompanies/types/companyUsers.type"
import { ProductData } from "@/features/products/types/product"
import { SearchIcon } from "lucide-react"

type FilterProps = {
  companyId?: string;
}

const Filters = ({ companyId }: FilterProps) => {
  const { filterParams, setFilterParams } = useFilterParams({
    fromInternalBase: internalBaseEquipments.find(({ value }) => value === 'false')
  });
  const { date } = useFilterDate(filterParams);

  const [searchBranch, setSearchBranch, instantSearchBranch] = useDebounce(filterParams.branch?.label || '');
  const [searchEstablishmentBranch, setSearchEstablishmentBranch] = useDebounce('')

  const { data: establishmentBranchOptions } = useEstablishmentCompanies({
    filters: { search: searchEstablishmentBranch, caModuleEnabled: true },
    mapper: Mapper.mapWithTransformer({ transformer: EstablishmentCompanyTransformer.optionTransformer() }),
  });

  const { data: clientCompanyBranchOptions } = useAvailableCompanyBranches({
    companyId,
    filters: {
      search: searchBranch,
    },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: equipmentOptions } = useCompanyEquipments({
    filters: { companyId },
    mapper: Mapper.mapToOptions<EquipmentData>({ labelFieldName: ['license_plate', 'serial_number'], valueFieldName: 'id' }),
  })

  const { data: companyUserOptions } = useCompanyUsers({
    filters: { companyId },
    mapper: Mapper.mapToOptions<CompanyUserData>({ labelFieldName: 'full_name', valueFieldName: 'id' }),
  })

  const { data: productOptions } = useProducts({
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  return (
    <div className="flex gap-x-2 z-50">
      <DatePickerWithRange date={date} setDate={(date) => setFilterParams((params) => ({ ...params, date }))} />

      <ListFilters
        filters={[
          {
            className: 'min-w-28 max-w-60',
            onChange: (establishmentCompany) => setFilterParams((prev) => ({ ...prev, establishmentCompany })),
            value: filterParams.establishmentCompany,
            onInputChange: (value) => setSearchEstablishmentBranch(value),
            options: [{ label: 'Todas', value: '' }, ...(establishmentBranchOptions || [])],
            placeholder: 'Estabelecimento',
          },

          {
            className: 'min-w-28 max-w-60',
            onChange: (e) =>
              setFilterParams((prev) => ({
                ...prev,
                fromInternalBase: e,
              })),
            value: filterParams.fromInternalBase,
            options: [{ label: 'Todas', value: '' }, ...(internalBaseEquipments || [])],
            placeholder: 'Exibir transações da base',
          },
        ]}
      />

      <SheetFilters
        filters={[
          {
            label: 'Equipamento',
            options: [
              {
                label: 'Todas',
                value: '',
                selected: !filterParams.equipment?.value,
                onClick: () => setFilterParams(({ equipment, ...prev }) => ({ ...prev }))
              },

              ...(equipmentOptions?.map(({ label, value }) => ({
                label,
                value,
                selected: filterParams.equipment?.value === value,
                onClick: () => setFilterParams((prev) => ({ ...prev, equipment: { label, value } }))
              })) || [])
            ]
          },

          {
            label: 'Produto',
            options: [
              {
                label: 'Todas',
                value: '',
                selected: !filterParams.product?.value,
                onClick: () => setFilterParams(({ product, ...prev }) => ({ ...prev }))
              },

              ...(productOptions?.map(({ label, value }) => ({
                label,
                value,
                selected: filterParams.product?.value === value,
                onClick: () => setFilterParams((prev) => ({ ...prev, product: { label, value } }))
              })) || [])
            ]
          },

          {
            label: 'Autor',
            options: [
              {
                label: 'Todas',
                value: '',
                selected: !filterParams.user?.value,
                onClick: () => setFilterParams(({ user, ...prev }) => ({ ...prev }))
              },

              ...(companyUserOptions?.map(({ label, value }) => ({
                label,
                value,
                selected: filterParams.user?.value === value,
                onClick: () => setFilterParams((prev) => ({ ...prev, user: { label, value } }))
              })) || [])
            ]
          },

          {
            label: 'Filial',
            options: [
              {
                label: 'Todas',
                value: '',
                selected: !filterParams.branch?.value,
                onClick: () => setFilterParams(({ branch, ...prev }) => ({ ...prev }))
              },

              ...(clientCompanyBranchOptions?.map(({ label, value }) => ({
                label,
                value,
                selected: filterParams.branch?.value === value,
                onClick: () => setFilterParams((prev) => ({ ...prev, branch: { label, value } }))
              })) || [])
            ],
            component: (
              <TextField
                variant="filter"
                LeftIcon={<SearchIcon size={14} />}
                placeholder="Pesquisar"
                className="mb-2"
                value={instantSearchBranch}
                onChange={(e) => setSearchBranch(e.currentTarget.value)}
              />
            ),
          },

        ]}
      />
    </div>
  )
}

export default Filters
